import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Container,
  TextField,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from '@mui/material';
import CustomRichTextEditor from '../../extensions/RichTextEditor';
import excuseApi from '../../api/excuseApi';
import { getCurrentUser } from '../../models/Account';
import { useLocation, useNavigate } from 'react-router-dom';
import Comment, { getComments } from '../../models/Comment';
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";

const getAvatarUrl = (obj) => {
  if (!obj) return `https://ui-avatars.com/api/?name=Anon&background=random`;

  if (obj.avatar) {
    return `${process.env.REACT_APP_STATIC_URL}${obj.avatar}`;
  }
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(obj.username || 'Anon')}&background=random`;
};

const ReplySection = ({ accountId }) => {
  const [content, setContent] = useState('');
  const [feelings, setFeelings] = useState([]);
  const [comments, setComments] = useState([]);
  const [pageFeelings, setPageFeelings] = useState(1);
  const [totalPagesFeelings, setTotalPagesFeelings] = useState(1);
  const [pageComments, setPageComments] = useState(1);
  const [totalPagesComments, setTotalPagesComments] = useState(1);
  const [tabValue, setTabValue] = useState('feelings');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const location = useLocation();
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL;
  const url = `${process.env.REACT_APP_STATIC_URL}/uploads/banner/banner.png`;
  const title = 'HỒI ÂM'

  // Load feelings
  const loadFeelings = async (pageNum = 1) => {
    try {
      const res = await excuseApi({
        endpoint: `/feelings?page=${pageNum}&per_page=10`,
        method: 'GET',
      });
      setFeelings(res.items || []);
      setTotalPagesFeelings(res.pages || 1);
    } catch (err) {
      throw err;
    }
  };
  // Load comments
  const loadComments = async (pageNum = 1) => {
    try {
      const res = await excuseApi({
        endpoint: `/comments-multi-target?target_type=article&page=${pageNum}&per_page=10`,
        method: 'GET',
      });
      setComments(res.items || []);
      setTotalPagesComments(res.pages || 1);
    } catch (err) {
      throw err;
    }
  };
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      getCurrentUser().then((u) => {
        setUser(u);
      });
    } else {
      setUser(null);
    }
  }, [location]);

  useEffect(() => {
    if (tabValue === 'comments') {
      loadComments(pageComments);
    } else {
      loadFeelings(pageFeelings);
    }
  }, [tabValue, pageComments, pageFeelings]);

  // Process content images/videos in feeling before post
  const processContentImages = async (html) => {
    const div = document.createElement('div');
    div.innerHTML = html;

    const mediaElements = div.querySelectorAll('img, video source');

    for (let el of mediaElements) {
      const src = el.getAttribute('src');

      if (src && src.startsWith('data:')) {
        try {
          const response = await fetch(src);
          const blob = await response.blob();
          const file = new File([blob], 'upload', { type: blob.type });

          const base64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          const isImage = file.type.startsWith('image/');
          const bodyKey = isImage ? 'image' : 'video';

          const res = await fetch(`${API_BASE}/upload-article-image`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ [bodyKey]: base64 }),
          });

          const data = await res.json();
          if (data.url) {
            el.setAttribute('src', data.url);
          }
        } catch (err) {
          throw err;
        }
      }
    }

    return div.innerHTML;
  };

  const handleCloseSnackbar = () => setSnackbarOpen(false);

  const handlePostFeeling = async () => {
    if (!user) {
      setSnackbarMessage('Bạn cần đăng nhập để đăng cảm nhận');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    try {
      const processedContent = await processContentImages(content);
      await excuseApi({
        endpoint: '/feelings',
        method: 'POST',
        data: { account_id: user.id, content: processedContent },
      });
      setContent('');
      loadFeelings(pageFeelings);
      setSnackbarMessage('Đã đăng cảm nhận thành công!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      throw err;
    }
  };

  const handleChangeTab = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 'comments') {
      setPageComments(1);
    } else {
      setPageFeelings(1);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ my: 4, bgcolor: '#fbf7e4', borderRadius: 2, p: 2 }}>
      <Paper
        elevation={2}
        sx={{
          maxWidth: 900,
          mx: "auto",
          mb: 5,
          px: 4,
          py: 3,
          borderRadius: 2,
          backgroundColor: "#faf7f1",
        }}
      >
        <Box display="flex" justifyContent="center" mb={1}>
          <FormatQuoteIcon sx={{ fontSize: 40, opacity: 0.3 }} />
        </Box>
        <Typography
          variant="h6"
          align="center"
          fontStyle="italic"
          sx={{ lineHeight: 1.6 }}
        >
          <Box component="span" fontWeight="bold" fontStyle="normal">
            Hồi âm
          </Box>{" "}
          là nơi tiếng lòng của thế hệ hôm nay và mai
          sau được cất lên qua những lời tri ân sâu sắc, niềm tự hào bất tận và ý chí vững bền trong
          hành trình gìn giữ, dựng xây Tổ quốc.
        </Typography>
      </Paper>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12} md={8}>
          {/* Đăng cảm nhận */}
          <Paper
            sx={{
              p: 3,
              mb: 4,
              bgcolor: '#8a0000',
              color: '#fbf7e4',
              borderRadius: 3,
              boxShadow: '0 4px 8px rgba(138,0,0,0.4)',
            }}
          >
            <Typography variant="h5" fontWeight="bold" gutterBottom>
              Đăng cảm nhận
            </Typography>
            <Box
              sx={{
                bgcolor: '#fbf7e4',
                borderRadius: 1,
                p: 1,
                color: '#4a5933',
                minHeight: 150,
                mb: 2,
              }}
            >
              <CustomRichTextEditor value={content} onChange={setContent} showToolbar={false} />
            </Box>
            <Button
              variant="contained"
              onClick={handlePostFeeling}
              sx={{
                bgcolor: 'black',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#364623' },
              }}
            >
              ĐĂNG CẢM NHẬN
            </Button>
          </Paper>

          {/* Tab chọn */}
          <Tabs
            value={tabValue}
            onChange={handleChangeTab}
            sx={{
              mb: 4,
              '& .MuiTabs-indicator': { bgcolor: '#8a0000' },
              '& .MuiTab-root': {
                fontWeight: 'bold',
                color: '#8a0000',
              },
              '& .Mui-selected': {
                color: '#4a5933',
                bgcolor: '#fbf7e4',
                borderRadius: 2,
              },
            }}
          >
            <Tab label="Cảm nhận mới" value="feelings" />
            <Tab label="Bình luận mới" value="comments" />
          </Tabs>

          {/* Tab Nội dung */}
          {tabValue === 'comments' && (
            <>
              {comments.map((comment) => (
                <Paper
                  key={comment.id}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: '#fbf7e4',
                    borderRadius: 3,
                    boxShadow: '0 1px 5px rgba(74,89,51,0.1)',
                    borderLeft: '6px solid #8a0000',
                  }}
                >
                  {/* Header comment: avatar + tên + ngày */}
                  <Box display="flex" alignItems="center" gap={2} mb={1}>
                    <Box
                      component="img"
                      src={getAvatarUrl(comment)}
                      alt={comment.username || 'Ẩn danh'}
                      sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                        {comment.username || 'Ẩn danh'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {new Intl.DateTimeFormat('vi-VN', {
                          timeZone: 'Asia/Ho_Chi_Minh',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        }).format(new Date(comment.created_at))}
                      </Typography>

                      {comment.article_id && comment.article_title && (
                        <Typography
                          variant="body2"
                          color="primary"
                          sx={{
                            mt: 0.5,
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                          onClick={() => navigate(`/articles/${comment.article_id}`)}
                        >
                          Từ bài viết: {comment.article_title}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Nội dung */}
                  <Typography
                    variant="body1"
                    sx={{ mt: 1, ml: 0, color: '#4a5933' }}
                    dangerouslySetInnerHTML={{ __html: comment.content }}
                  />

                  <CommentList
                    targetType="comment"
                    targetId={comment.id}
                    parentId={comment.id}
                    accountId={accountId}
                    user={user}
                    allowOnlyOneReply={false}
                  />
                </Paper>
              ))}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                {Array.from({ length: totalPagesComments }, (_, i) => (
                  <Button
                    key={i}
                    variant={pageComments === i + 1 ? 'contained' : 'outlined'}
                    onClick={() => setPageComments(i + 1)}
                    sx={{
                      mr: 1,
                      bgcolor: pageComments === i + 1 ? '#8a0000' : 'transparent',
                      color: pageComments === i + 1 ? '#fbf7e4' : '#8a0000',
                      fontWeight: 'bold',
                      '&:hover': {
                        bgcolor: '#4a5933',
                        color: '#fbf7e4',
                      },
                    }}
                  >
                    {i + 1}
                  </Button>
                ))}
              </Box>
            </>
          )}

          {tabValue === 'feelings' && (
            <>
              {feelings.map((feeling) => (
                <Paper
                  key={feeling.id}
                  sx={{
                    p: 3,
                    mb: 3,
                    bgcolor: '#fbf7e4',
                    borderRadius: 3,
                    boxShadow: '0 1px 5px rgba(74,89,51,0.1)',
                    borderLeft: '6px solid #8a0000',
                  }}
                >

                  <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Box
                      component="img"
                      src={getAvatarUrl(feeling)}
                      alt={feeling.username || 'Ẩn danh'}
                      sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <Box>
                      <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                        {feeling.username || 'Ẩn danh'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        {new Intl.DateTimeFormat('vi-VN', {
                          timeZone: 'Asia/Ho_Chi_Minh',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                        }).format(new Date(feeling.created_at))}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: '#fffbe6',
                      p: 2,
                      borderRadius: 2,
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                    }}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: feeling.content }}
                      style={{ color: '#4a5933', fontSize: '1rem', lineHeight: 1.6 }}
                    />
                  </Box>
                  <CommentList
                    targetType="feeling"
                    targetId={feeling.id}
                    parentId={feeling.id}
                    accountId={accountId}
                    user={user}
                    allowOnlyOneReply={true}
                  />
                </Paper>
              ))}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                {Array.from({ length: totalPagesFeelings }, (_, i) => (
                  <Button
                    key={i}
                    variant={pageFeelings === i + 1 ? 'contained' : 'outlined'}
                    onClick={() => setPageFeelings(i + 1)}
                    sx={{
                      mr: 1,
                      bgcolor: pageFeelings === i + 1 ? '#8a0000' : 'transparent',
                      color: pageFeelings === i + 1 ? '#fbf7e4' : '#8a0000',
                      fontWeight: 'bold',
                      '&:hover': {
                        bgcolor: '#4a5933',
                        color: '#fbf7e4',
                      },
                    }}
                  >
                    {i + 1}
                  </Button>
                ))}
              </Box>
            </>
          )}
        </Grid>
      </Grid>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: '400px',
            fontSize: '1rem',
            fontWeight: 'bold',
            textAlign: 'center',
            bgcolor: snackbarSeverity === 'success' ? '#4caf50' : '#f44336',
            color: '#fff',
            boxShadow: 3,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReplySection;

// CommentList component cho cả comment và feelings
const CommentList = ({
  targetType,
  targetId,
  accountId,
  parentId,
  user,
  allowOnlyOneReply = false, // thêm prop giới hạn phản hồi
}) => {
  const [commentList, setCommentList] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);

  // Dùng để lưu các phản hồi của comment cha
  const [subVisibleCount, setSubVisibleCount] = useState({});
  const [replyText, setReplyText] = useState({}); // text phản hồi từng comment cha
  const [loadingSubcomments, setLoadingSubcomments] = useState({}); // loading subcomments từng comment cha

  // Load comment chính (comment con khi targetType = 'comment')
  // hoặc comment chính khi targetType = 'feeling' hoặc 'article'
  const loadComments = async () => {
    setLoadingComments(true);
    try {
      const res = await getComments({ target_type: targetType, target_id: targetId });
      const withExtras = (res.items || []).map((c) => ({
        ...c,
        subcomments: [],
        subLoaded: false,
      }));
      setCommentList(withExtras);
    } catch (err) {
      throw err;
    }
    setLoadingComments(false);
  };

  useEffect(() => {
    if (targetType && targetId) {
      loadComments();
    } else {
      setCommentList([]);
    }
  }, [targetType, targetId]);

  useEffect(() => {
    commentList.forEach((c) => {
      if (!c.subLoaded) {
        loadSubcomments(c.id);
      }
    });
  }, [commentList]);

  // Load subcomments cho comment cha
  const loadSubcomments = async (parentId) => {
    setLoadingSubcomments((prev) => ({ ...prev, [parentId]: true }));
    try {
      const res = await excuseApi({
        endpoint: `/comments/${parentId}/subcomments`,
        method: 'GET',
      });

      setCommentList((prev) =>
        prev.map((c) =>
          c.id === parentId ? { ...c, subcomments: res || [], subLoaded: true } : c
        )
      );
      // ensure initial visible count
      setSubVisibleCount((prev) => ({ ...prev, [parentId]: 5 }));
    } catch (err) {
      throw err;
    }
    setLoadingSubcomments((prev) => ({ ...prev, [parentId]: false }));
  };

  // Kiểm tra xem user đã phản hồi comment cha chưa
  const hasReplied = (parentId) => {
    if (!user) return false;
    const comment = commentList.find((c) => c.id === parentId);
    if (!comment || !comment.subcomments) return false;
    return comment.subcomments.some((sub) => sub.account_id === user.id);
  };

  // Gửi phản hồi subcomment
  const handleReplySubmit = async (parentId) => {
    if (!user) {
      setSnackbarMessage('Bạn cần đăng nhập để phản hồi'); 
      setSnackbarSeverity('error'); 
      setSnackbarOpen(true);
      return;
    }
    const reply = replyText[parentId];
    if (!reply || reply.trim() === '') {
      setSnackbarMessage('Nội dung phản hồi không được để trống'); 
      setSnackbarSeverity('error'); 
      setSnackbarOpen(true);
      return;
    }
    try {
      await Comment.createComment({
        target_type: 'comment',
        target_id: parentId,
        parent_id: parentId,
        account_id: user.id,
        content: reply,
      });
      setReplyText((prev) => ({ ...prev, [parentId]: '' }));

      // Reload subcomments cho parent
      loadSubcomments(parentId);
    } catch (err) {
      throw err;
    }
  };

  // Thêm comment mới (comment chính hoặc comment cho feeling, article)
  const [newCommentText, setNewCommentText] = useState('');
  const [postingComment, setPostingComment] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  const handleNewCommentSubmit = async (parentId = null, targetType = null) => {
    if (!user) {
      setSnackbarMessage('Bạn cần đăng nhập để bình luận'); 
      setSnackbarSeverity('error'); 
      setSnackbarOpen(true);
      return;
    }

    const text = (newCommentText || '').trim();
    if (!text) {
      setSnackbarMessage('Nội dung bình luận không được để trống'); 
      setSnackbarSeverity('error'); 
      setSnackbarOpen(true);
      return;
    }

    setPostingComment(true);
    try {
      let type = 'comment';
      if (targetType === 'feeling') {
        type = 'feeling';
      }

      const payload = {
        target_type: type,
        target_id: parentId ? parentId : targetId,
        account_id: user.id,
        content: text,
      };

      if(type != 'feeling'){
        if (parentId) {
          payload.parent_id = parentId;
        }
      }

      const res = await excuseApi({
        endpoint: '/comments',
        method: 'POST',
        data: payload,
      });
      setNewCommentText('');

      if (res) {
        await loadSubcomments(parentId);
        await loadComments();
      }
    } catch (err) {
      throw err;
    } finally {
      setPostingComment(false);
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ color: '#4a5933' }}>
        {loadingComments ? 'Đang tải bình luận...' : 'Bình luận:'}
      </Typography>

      {/* Form thêm comment mới (kiểu giống ArticleDetail) */}
      {!allowOnlyOneReply && user && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Viết bình luận..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            sx={{
              bgcolor: '#fff',
              borderRadius: 1,
              border: '1px solid #8a0000',
              '& .MuiInputBase-input': {
                color: '#4a5933',
              },
            }}
          />
          <Button
            size="small"
            sx={{
              mt: 1,
              bgcolor: '#8a0000',
              color: '#fbf7e4',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#4a5933' },
            }}
            variant="contained"
            onClick={() => handleNewCommentSubmit(parentId, targetType)}
            disabled={postingComment}
          >
            Gửi bình luận
          </Button>
        </Box>
      )}

      {/* Nếu cho phép chỉ 1 phản hồi thì form comment chỉ hiện khi chưa có phản hồi */}
      {allowOnlyOneReply && user && !hasReplied(targetId) && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={2}
            size="small"
            placeholder="Viết bình luận..."
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            sx={{
              bgcolor: '#fff',
              borderRadius: 1,
              border: '1px solid #8a0000',
              '& .MuiInputBase-input': {
                color: '#4a5933',
              },
            }}
          />
          <Button
            size="small"
            sx={{
              mt: 1,
              bgcolor: '#8a0000',
              color: '#fbf7e4',
              fontWeight: 'bold',
              '&:hover': { bgcolor: '#4a5933' },
            }}
            variant="contained"
            onClick={() => handleNewCommentSubmit(parentId, targetType)}
            disabled={postingComment}
          >
            Gửi bình luận
          </Button>
        </Box>
      )}

      {/* Danh sách comment chính */}
      {commentList.map((c) => (
        <Paper
          key={c.id}
          elevation={1}
          sx={{
            p: 2,
            mb: 2,
            bgcolor: '#fbf7e4',
            borderRadius: 2,
            borderLeft: '5px solid #8a0000',
            boxShadow: '0 1px 3px rgba(138,0,0,0.2)',
          }}
        >
          {/* Header: avatar + name + date */}
          <Box display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              component="img"
              src={getAvatarUrl(c)}
              alt={c.username || 'Ẩn danh'}
              sx={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }}
            />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" color="#4a5933" sx={{ lineHeight: 1 }}>
                {c.username || 'Ẩn danh'}
              </Typography>
              <Typography variant="caption" color="#8a0000" sx={{ lineHeight: 1 }}>
                {new Intl.DateTimeFormat('vi-VN', {
                  timeZone: 'Asia/Ho_Chi_Minh',
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }).format(new Date(c.created_at))}
              </Typography>
            </Box>
          </Box>

          <Typography
            variant="body1"
            sx={{ mt: 1, color: '#4a5933' }}
            dangerouslySetInnerHTML={{ __html: c.content }}
          />

          {/* Xem phản hồi
          {!c.subLoaded && (
            <Button
              size="small"
              sx={{ mt: 1, ml: 6 }}
              onClick={() => loadSubcomments(c.id)}
            >
              Xem phản hồi
            </Button>
          )} */}

          {/* Subcomments */}
          {c.subLoaded && (
            <Box sx={{ ml: 6, mt: 2 }}>
              {c.subcomments
                .slice(0, subVisibleCount[c.id] || 5)
                .map((sub) => (
                  <Paper
                    key={sub.id}
                    elevation={0}
                    sx={{
                      p: 1,
                      mb: 1,
                      bgcolor: '#f1f1f1',
                      borderRadius: 1,
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                      <Box
                        component="img"
                        src={getAvatarUrl(sub)}
                        alt={sub.username || 'Ẩn danh'}
                        sx={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold" sx={{ lineHeight: 1 }}>
                          {sub.username || 'Ẩn danh'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                          {new Intl.DateTimeFormat('vi-VN', {
                            timeZone: 'Asia/Ho_Chi_Minh',
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                          }).format(new Date(sub.created_at))}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ ml: 5 }}>
                      <Box
                        sx={{
                          bgcolor: '#fffbe6',
                          p: 2,
                          borderRadius: 2,
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        }}
                      >
                        {sub.content}
                      </Box>
                    </Typography>
                  </Paper>
                ))}

              {subVisibleCount[c.id] < c.subcomments.length && (
                <Button
                  size="small"
                  sx={{ color: '#8a0000', fontWeight: 'bold' }}
                  onClick={() =>
                    setSubVisibleCount((prev) => ({
                      ...prev,
                      [c.id]: (prev[c.id] || 5) + 5,
                    }))
                  }
                >
                  Xem thêm phản hồi
                </Button>
              )}

              {/* Form nhập phản hồi */}
              {user && (
                <Box sx={{ mt: 1, ml: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Viết phản hồi..."
                    value={replyText[c.id] || ''}
                    onChange={(e) =>
                      setReplyText((prev) => ({
                        ...prev,
                        [c.id]: e.target.value,
                      }))
                    }
                    sx={{
                      bgcolor: '#fff',
                      borderRadius: 1,
                      border: '1px solid #8a0000',
                      '& .MuiInputBase-input': {
                        color: '#4a5933',
                      },
                    }}
                  />
                  <Button
                    size="small"
                    sx={{
                      mt: 1,
                      bgcolor: '#8a0000',
                      color: '#fbf7e4',
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#4a5933' },
                    }}
                    onClick={() => handleReplySubmit(c.id)}
                    disabled={!replyText[c.id] || replyText[c.id].trim() === ''}
                  >
                    Gửi phản hồi
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      ))}
    </Box>
  );
};
